import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';

const CONTROL =
  'block w-full rounded-lg border border-line bg-surface px-2.5 text-sm text-ink placeholder:text-muted focus:border-accent transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:bg-subtle disabled:text-muted aria-[invalid=true]:border-danger';

export function Input({ className, ...rest }: ComponentProps<'input'>) {
  return <input {...rest} className={cn(CONTROL, 'h-9', className)} />;
}

export function Textarea({ className, ...rest }: ComponentProps<'textarea'>) {
  return <textarea {...rest} className={cn(CONTROL, 'py-1.5', className)} />;
}

export function Select({ className, children, ...rest }: ComponentProps<'select'>) {
  return (
    <select {...rest} className={cn(CONTROL, 'h-9 pr-7', className)}>
      {children}
    </select>
  );
}

export function Checkbox({ label, className, ...rest }: ComponentProps<'input'> & { label?: ReactNode }) {
  return (
    <label className={cn('inline-flex items-center gap-2 text-sm text-ink', className)}>
      <input type="checkbox" {...rest} className="h-4 w-4 rounded border-line accent-(--color-accent)" />
      {label}
    </label>
  );
}

/** Label + control + hint/error. */
export function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-xs font-medium text-ink-2">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-muted">{hint}</span>
      ) : null}
    </label>
  );
}
